import {
    ReactNode,
    useState,
    useRef,
    useEffect,
    useLayoutEffect,
    createContext,
    useContext,
    useCallback,
    useMemo,
  } from 'react'
  import { createPortal } from 'react-dom'
  import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
  
  const DropdownContext = createContext<{ close: () => void } | null>(null)
  
  /** Scroll containers that move the trigger (e.g. `main { overflow-auto }`, not only `window`). */
  function getScrollRepositionTargets(node: HTMLElement | null): EventTarget[] {
    const targets: EventTarget[] = [window]
    const seen = new Set<EventTarget>([window])
    let el: HTMLElement | null = node
    while (el) {
      const style = window.getComputedStyle(el)
      const overflowScrollable = /(auto|scroll|overlay)/.test(
        `${style.overflow}${style.overflowX}${style.overflowY}`
      )
      const hasVerticalScroll = el.scrollHeight > el.clientHeight + 1
      const hasHorizontalScroll = el.scrollWidth > el.clientWidth + 1
      if (overflowScrollable || hasVerticalScroll || hasHorizontalScroll) {
        if (!seen.has(el)) {
          targets.push(el)
          seen.add(el)
        }
      }
      el = el.parentElement
    }
    if (document.documentElement && !seen.has(document.documentElement)) {
      targets.push(document.documentElement)
    }
    if (document.body && !seen.has(document.body)) {
      targets.push(document.body)
    }
    return targets
  }
  
  export interface DropdownOption {
    value: string
    label: string
  }
  
  export interface DropdownTriggerApi {
    openMenu: () => void
    closeMenu: () => void
    toggle: () => void
  }
  
  interface DropdownProps {
    /** Custom trigger. Render prop receives open state and menu controls (use `toggle` / `openMenu` on your button). */
    trigger: ReactNode | ((open: boolean, api: DropdownTriggerApi) => ReactNode)
    /** Dropdown panel content (e.g. Dropdown.Item or Dropdown.Option list). */
    children: ReactNode
    /** Align menu to trigger: start (left) or end (right). */
    placement?: 'bottom-start' | 'bottom-end'
    /** When true, render menu in a portal (overlay; pass when needed to avoid layout/overflow issues). */
    usePortal?: boolean
    /** Min width of the menu panel (e.g. '120px', '200px'). */
    minWidth?: string
    /** Class for the trigger wrapper. */
    className?: string
    /** Class for the menu panel. */
    menuClassName?: string
    /** When usePortal and placement=bottom-end, use this width (px) so menu right edge aligns with trigger. */
    menuWidthPx?: number
    /** Role for the menu panel: 'listbox' (select) or 'menu' (actions). */
    role?: 'listbox' | 'menu'
    /** Callback when open state changes (e.g. for accessibility). */
    onOpenChange?: (open: boolean) => void
  }
  
  export function Dropdown({
    trigger,
    children,
    placement = 'bottom-start',
    usePortal,
    minWidth = 'min-w-[120px]',
    className = '',
    menuClassName = '',
    menuWidthPx,
    role = 'menu',
    onOpenChange,
  }: DropdownProps) {
    const [open, setOpen] = useState(false)
    const [position, setPosition] = useState({ top: 0, left: 0 })
    const wrapperRef = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
  
    const MENU_WIDTH_PX = 120
    const effectiveMenuWidth = menuWidthPx ?? MENU_WIDTH_PX
  
    const updatePosition = useCallback(() => {
      if (!wrapperRef.current) return
      const rect = wrapperRef.current.getBoundingClientRect()
      const viewportPadding = 8
      const fallbackMenuHeight = 260
      const menuEl = menuRef.current
      const measured =
        menuEl?.getBoundingClientRect().height ?? menuEl?.offsetHeight ?? fallbackMenuHeight
      const menuHeight = measured > 0 ? measured : fallbackMenuHeight
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      const openUpwards = spaceBelow < menuHeight + viewportPadding && spaceAbove > spaceBelow
      const unclampedTop = openUpwards ? rect.top - menuHeight - 4 : rect.bottom + 4
      const top = Math.max(viewportPadding, Math.min(unclampedTop, window.innerHeight - menuHeight - viewportPadding))
      const unclampedLeft = placement === 'bottom-end' ? rect.right - effectiveMenuWidth : rect.left
      const left = Math.max(viewportPadding, Math.min(unclampedLeft, window.innerWidth - effectiveMenuWidth - viewportPadding))
      setPosition({
        top,
        left,
      })
    }, [effectiveMenuWidth, placement])
  
    const openMenu = () => {
      setOpen(true)
      onOpenChange?.(true)
      if (usePortal === true) {
        requestAnimationFrame(() => {
          requestAnimationFrame(updatePosition)
        })
      }
    }
  
    const closeMenu = () => {
      setOpen(false)
      onOpenChange?.(false)
    }
  
    const toggle = () => {
      if (open) closeMenu()
      else openMenu()
    }
  
    const triggerApi: DropdownTriggerApi = {
      openMenu,
      closeMenu,
      toggle,
    }
  
    useLayoutEffect(() => {
      if (!open || usePortal !== true) return
      updatePosition()
    }, [open, usePortal, updatePosition])
  
    useEffect(() => {
      if (!open || usePortal !== true) return
      const onViewportChange = () => updatePosition()
      const scrollTargets = getScrollRepositionTargets(wrapperRef.current)
      window.addEventListener('resize', onViewportChange)
      scrollTargets.forEach((target) => {
        target.addEventListener('scroll', onViewportChange, { capture: true })
      })
      const vv = window.visualViewport
      vv?.addEventListener('scroll', onViewportChange)
      vv?.addEventListener('resize', onViewportChange)
      return () => {
        window.removeEventListener('resize', onViewportChange)
        scrollTargets.forEach((target) => {
          target.removeEventListener('scroll', onViewportChange, { capture: true })
        })
        vv?.removeEventListener('scroll', onViewportChange)
        vv?.removeEventListener('resize', onViewportChange)
      }
    }, [open, usePortal, updatePosition])
  
    useEffect(() => {
      if (!open || usePortal !== true) return
      const menu = menuRef.current
      if (!menu || typeof ResizeObserver === 'undefined') return
      const ro = new ResizeObserver(() => updatePosition())
      ro.observe(menu)
      return () => ro.disconnect()
    }, [open, usePortal, updatePosition])
  
    useEffect(() => {
      if (!open) return
      const handler = (e: MouseEvent) => {
        const target = e.target as Node
        if (wrapperRef.current?.contains(target) || menuRef.current?.contains(target)) return
        setOpen(false)
        onOpenChange?.(false)
      }
      document.addEventListener('mousedown', handler)
      return () => document.removeEventListener('mousedown', handler)
    }, [onOpenChange, open])
  
    const panelClasses = `flex flex-col overflow-hidden rounded-xl border border-border-light bg-background-white ${minWidth} ${menuClassName}`.trim()
    const panelPositionClasses = usePortal
      ? ''
      : 'absolute left-0 top-full z-20 mt-1'
  
    const panelContent = open ? (
      <DropdownContext.Provider value={{ close: closeMenu }}>
        <div
          ref={menuRef}
          role={role}
          className={`${panelClasses} ${panelPositionClasses}`.trim()}
          style={usePortal === true ? { position: 'fixed', top: position.top, left: position.left, zIndex: 9999 } : undefined}
        >
          {children}
        </div>
      </DropdownContext.Provider>
    ) : null
  
    const inlinePanel = usePortal !== true && panelContent
    const portaledPanel = usePortal === true && createPortal(panelContent, document.body)
  
    return (
      <div ref={wrapperRef} className={`relative inline-block ${className}`.trim()}>
        <div onClick={e => e.stopPropagation()} className="contents">
          {typeof trigger === 'function' ? (
            trigger(open, triggerApi)
          ) : (
            <div onClick={toggle} className="contents">
              {trigger}
            </div>
          )}
        </div>
        {inlinePanel}
        {portaledPanel}
      </div>
    )
  }
  
  interface DropdownItemProps {
    children: ReactNode
    onClick?: () => void
    /** Show border above this item. */
    divider?: boolean
    className?: string
    role?: 'option' | 'menuitem'
    ariaSelected?: boolean
  }
  
  function DropdownItem({
    children,
    onClick,
    divider = false,
    className = '',
    role = 'menuitem',
    ariaSelected,
  }: DropdownItemProps) {
    const ctx = useContext(DropdownContext)
  
    const handleClick = () => {
      ctx?.close()
      onClick?.()
    }
  
    return (
      <button
        type="button"
        role={role}
        aria-selected={ariaSelected}
        onClick={handleClick}
        className={`block w-full shrink-0 cursor-pointer px-4 py-2.5 text-left text-sm font-regular text-text-primary hover:bg-primary/10 first:rounded-t-xl last:rounded-b-xl ${divider ? 'border-t border-border-light' : ''} ${className}`.trim()}
      >
        {children}
      </button>
    )
  }
  
  /** Select-style: value + options + onChange. Renders trigger with selected label and chevron, and option list. */
  interface DropdownSelectProps {
    value: string
    options: DropdownOption[]
    onChange: (value: string) => void
    placeholder?: string
    /** Width of trigger (e.g. 'w-[200px]'). */
    triggerWidth?: string
    /** When true, render options in a portal (overlay; pass when needed so dropdown doesn't expand layout). */
    usePortal?: boolean
    placement?: 'bottom-start' | 'bottom-end'
    className?: string
    /** When true, shows red border and ring (error state). */
    error?: boolean
    disabled?: boolean
    /** Scroll the options panel near the bottom to load more (e.g. paginated API). */
    onReachEnd?: () => void
    /** When false, `onReachEnd` is not called. */
    hasMore?: boolean
    /** Show a loading row at the bottom while the next page loads. */
    isFetchingMore?: boolean
    /** When true (default), show a search field and filter options by label (client-side). */
    searchable?: boolean
    /** Placeholder for the search field when `searchable` is true. */
    searchPlaceholder?: string
    /** Portal positioning width in px (defaults from `w-[NNNpx]` in `triggerWidth`). */
    menuWidthPx?: number
    /** Notify parent when menu opens/closes. */
    onOpenChange?: (open: boolean) => void
  }
  
  function filterOptionsByQuery(options: DropdownOption[], query: string): DropdownOption[] {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }
  
  function DropdownSelect({
    value,
    options,
    onChange,
    placeholder = 'Select...',
    triggerWidth = 'w-[200px]',
    usePortal,
    placement = 'bottom-start',
    className = '',
    error = false,
    disabled = false,
    onReachEnd,
    hasMore = false,
    isFetchingMore = false,
    searchable = true,
    searchPlaceholder = 'Search…',
    menuWidthPx: menuWidthPxProp,
    onOpenChange,
  }: DropdownSelectProps) {
    const listRef = useRef<HTMLDivElement>(null)
    const comboboxInputRef = useRef<HTMLInputElement>(null)
    const listboxId = useRef(`dropdown-listbox-${Math.random().toString(36).slice(2, 10)}`)
    const [searchQuery, setSearchQuery] = useState('')
  
    const menuWidthPx = useMemo(() => {
      if (menuWidthPxProp != null) return menuWidthPxProp
      const m = /w-\[(\d+)px\]/.exec(triggerWidth)
      return m ? Number(m[1]) : undefined
    }, [menuWidthPxProp, triggerWidth])
  
    const selectedLabel = options.find(o => o.value === value)?.label ?? null
    const errorClasses = error ? 'border-red-500' : 'border-border'
    const disabledClasses = disabled ? 'cursor-not-allowed opacity-60' : ''
    const focusRing = error
      ? 'focus-within:ring-red-500 focus-within:border-red-500'
      : 'focus-within:ring-primary focus-within:border-primary'
  
    const filteredOptions = useMemo(
      () => (searchable ? filterOptionsByQuery(options, searchQuery) : options),
      [options, searchQuery, searchable]
    )
  
    const handleOpenChange = useCallback(
      (isOpen: boolean) => {
        if (!searchable) return
        setSearchQuery('')
        if (isOpen) {
          queueMicrotask(() => comboboxInputRef.current?.focus())
        }
      },
      [searchable]
    )
  
    const handleListScroll = useCallback(() => {
      if (!onReachEnd || !hasMore || isFetchingMore) return
      const el = listRef.current
      if (!el) return
      // Only attempt infinite loading when the panel is actually scrollable.
      // This prevents eager "page 2" fetches immediately on open when the first page doesn't overflow.
      if (el.scrollHeight <= el.clientHeight + 1) return
      const threshold = 48
      if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) onReachEnd()
    }, [onReachEnd, hasMore, isFetchingMore])
  
    return (
      <Dropdown
        usePortal={usePortal}
        placement={placement}
        minWidth={triggerWidth}
        className={triggerWidth}
        menuWidthPx={menuWidthPx}
        role="listbox"
        onOpenChange={(open) => {
          if (searchable) handleOpenChange(open)
          onOpenChange?.(open)
        }}
        trigger={(open, api) => {
          if (!searchable) {
            return (
              <button
                type="button"
                disabled={disabled}
                className={`flex w-full items-center justify-between gap-2 rounded-lg border bg-background-white px-4 py-2.5 text-left text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-offset-0 transition-colors ${errorClasses} ${error ? 'focus:ring-red-500 focus:border-red-500' : 'focus:ring-primary focus:border-primary'} ${disabledClasses} ${className}`.trim()}
                aria-expanded={open}
                aria-haspopup="listbox"
                aria-label={placeholder}
                aria-disabled={disabled}
                onClick={api.toggle}
              >
                <span className={selectedLabel ? 'min-w-0 truncate' : 'text-text-muted'}>
                  {selectedLabel ?? placeholder}
                </span>
                <ChevronDownIcon className="h-5 w-5 shrink-0 text-text-muted" aria-hidden />
              </button>
            )
          }
  
          const inputValue = open ? searchQuery : (selectedLabel ?? '')
          return (
            <div
              className={`flex min-w-0 items-stretch rounded-lg border bg-background-white transition-colors focus-within:outline-none focus-within:ring-1 focus-within:ring-offset-0 ${errorClasses} ${focusRing} ${disabledClasses} ${className}`.trim()}
            >
              <div className="relative min-w-0 flex-1">
                {open && (
                  <MagnifyingGlassIcon
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
                    aria-hidden
                  />
                )}
                <input
                  ref={comboboxInputRef}
                  type="text"
                  role="combobox"
                  aria-expanded={open}
                  aria-haspopup="listbox"
                  aria-controls={listboxId.current}
                  aria-autocomplete="list"
                  disabled={disabled}
                  readOnly={!open}
                  value={inputValue}
                  onChange={(e) => {
                    if (open) setSearchQuery(e.target.value)
                  }}
                  onFocus={() => {
                    if (!disabled && !open) api.openMenu()
                  }}
                  placeholder={open ? searchPlaceholder : placeholder}
                  autoComplete="off"
                  aria-label={open ? searchPlaceholder : placeholder}
                  aria-invalid={error}
                  className={`w-full min-w-0 bg-transparent py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none disabled:cursor-not-allowed ${open ? 'pl-9 pr-2' : 'truncate px-4 pr-2'} ${!inputValue && !open ? 'text-text-muted' : ''}`.trim()}
                />
              </div>
              <button
                type="button"
                tabIndex={-1}
                disabled={disabled}
                className="inline-flex shrink-0 items-center px-2.5 text-text-muted hover:text-text-primary disabled:opacity-60"
                aria-label="Toggle options"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  api.toggle()
                }}
              >
                <ChevronDownIcon className="h-5 w-5" aria-hidden />
              </button>
            </div>
          )
        }}
      >
        <div
          id={listboxId.current}
          ref={listRef}
          onScroll={onReachEnd ? handleListScroll : undefined}
          className="max-h-[min(240px,calc(100vh-120px))] overflow-y-auto overscroll-contain"
        >
          {filteredOptions.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-text-secondary">
              {searchable && searchQuery.trim() ? 'No matches' : 'No options'}
            </p>
          )}
          {filteredOptions.map((option, i) => (
            <DropdownItem
              key={option.value}
              role="option"
              ariaSelected={value === option.value}
              divider={i > 0}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </DropdownItem>
          ))}
          {isFetchingMore && (
            <div className="border-t border-border-light px-4 py-2 text-left text-xs text-text-secondary">
              Loading more…
            </div>
          )}
          {!isFetchingMore && hasMore && onReachEnd && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onReachEnd()
              }}
              className="w-full border-t border-border-light px-4 py-2 text-left text-xs font-medium text-blue-500 hover:text-blue-600 hover:underline"
            >
              Load more
            </button>
          )}
        </div>
      </Dropdown>
    )
  }
  
  Dropdown.Item = DropdownItem
  Dropdown.Select = DropdownSelect
  export default Dropdown